import { useEffect, useReducer, useState } from 'react';
import { getValueList as getValueReferenceList } from '../../../../../../../../sources/value';
import translations from '../../../../../../../../translations';
import type {
    PageComponentValidationAPI,
    PageComponentValidationType,
} from '../../../../../../../../types/validation';
import type { ContentType, ValueElementIdAPI } from '../../../../../../../../types/value';
import {
    isEmptyGuid,
    isFullValueReference,
    isValueReference,
} from '../../../../../../../../utils/guard';
import FormGroup from '../../../../../../../generic/FormGroup';
import Toggle from '../../../../../../../inputs/Toggle';
import { useNotificationDispatch } from '../../../../../../../notifications/NotificationProvider';
import ConfigSection from '../../ConfigSection';
import ValidationForm from './ValidationForm';
import ValidationList from './ValidationList';

interface Props {
    isRequired: boolean;
    updateRequired: (isRequired: boolean) => void;
    valueElementId: ValueElementIdAPI | null;
    validations: PageComponentValidationAPI[];
    updateValidations: (validations: PageComponentValidationAPI[]) => void;
}

interface State {
    viewState: 'list' | 'form' | 'hidden';
    selectedValidationIndex: number | null;
    contentType: ContentType | null;
}

export type ValidationAction =
    | {
          type: 'savedValidation';
          payload: { valueElementId: ValueElementIdAPI | null };
      }
    | {
          type: 'cancelValidation';
          payload: { valueElementId: ValueElementIdAPI | null };
      }
    | {
          type: 'setContentType';
          payload: { contentType: ContentType | null };
      }
    | {
          type: 'addValidationClick';
      }
    | {
          type: 'editValidationClick';
          payload: { selectedValidationIndex: number };
      };

const reducer = (state: State, action: ValidationAction): State => {
    switch (action.type) {
        case 'savedValidation':
        case 'cancelValidation':
            return {
                ...state,
                viewState: action.payload.valueElementId === null ? 'hidden' : 'list',
                selectedValidationIndex: null,
            };

        case 'setContentType': {
            const { contentType } = action.payload;
            const viewState =
                contentType === null
                    ? 'hidden' //  validations are not applicable
                    : 'list';

            return {
                ...state,
                viewState,
                contentType,
            };
        }

        case 'addValidationClick':
            return {
                ...state,
                viewState: 'form',
                selectedValidationIndex: null,
            };

        case 'editValidationClick':
            return {
                ...state,
                viewState: 'form',
                selectedValidationIndex: action.payload.selectedValidationIndex,
            };
    }
};

const filterInvalidValidations = (
    contentType: ContentType | null,
    validations: PageComponentValidationAPI[],
) => {
    return validations.filter((validation) => {
        // Always keep validations with a value reference because we don't know what type of value they refer to
        if (isValueReference(validation.value)) {
            return true;
        }

        const minMax: PageComponentValidationType[] = ['Minimum', 'Maximum'];
        switch (contentType) {
            case null:
            case 'ContentBoolean':
            case 'ContentObject':
                return false;

            case 'ContentString':
            case 'ContentPassword':
            case 'ContentEncrypted':
            case 'ContentContent':
            case 'ContentCode': {
                if (minMax.includes(validation.type)) {
                    return typeof validation.value === 'number';
                }
                return true;
            }

            case 'ContentDate':
            case 'ContentDateTime':
                return (
                    minMax.includes(validation.type) &&
                    typeof validation.value === 'string' &&
                    Number.isNaN(Number(validation.value))
                );

            case 'ContentNumber':
            case 'ContentList':
                return minMax.includes(validation.type) && typeof validation.value === 'number';
            default:
                return false;
        }
    });
};

const ValidationConfig = ({
    isRequired,
    updateRequired,
    valueElementId,
    validations,
    updateValidations,
}: Props) => {
    const initialState: State = {
        viewState: 'hidden',
        selectedValidationIndex: null,
        contentType: null,
    };

    const [state, dispatchValidations] = useReducer(reducer, initialState);
    const [isLoadingValueReferences, setIsLoadingValueReferences] = useState(false);

    const dispatchNotification = useNotificationDispatch();

    const supportsRequiredValidationOnly =
        state.contentType === 'ContentBoolean' ||
        state.contentType === 'ContentObject' ||
        state.contentType === 'ContentList';

    const renderRequiredToggle = () => (
        <FormGroup>
            <label htmlFor="is-required-toggle">
                <Toggle
                    id="is-required-toggle"
                    isOn={isRequired}
                    onChange={() => {
                        updateRequired(!isRequired);
                    }}
                />
                {translations.COMMON_required}
            </label>
        </FormGroup>
    );

    const onSave = (updatedValidation: PageComponentValidationAPI) => {
        if (state.selectedValidationIndex === null) {
            updateValidations([...validations, updatedValidation]);
        } else {
            updateValidations(
                validations.map((validation, index) =>
                    index === state.selectedValidationIndex ? updatedValidation : validation,
                ),
            );
        }

        dispatchValidations({ type: 'savedValidation', payload: { valueElementId } });
    };

    const onCancel = () => {
        dispatchValidations({ type: 'cancelValidation', payload: { valueElementId } });
    };

    const selectedValidation =
        state.selectedValidationIndex === null
            ? null
            : (validations[state.selectedValidationIndex] ?? null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        // Track if this effect is still current (not superseded by a new effect)
        let isStale = false;

        // Get the content type of the value bound to the component state
        const getContentType = async (
            valueElementId: ValueElementIdAPI,
        ): Promise<ContentType | null> => {
            const valueReferences = await getValueReferenceList({ id: valueElementId.id });
            const selectedValueReference = valueReferences.find((ref) => {
                if (
                    valueElementId.typeElementPropertyId === null ||
                    valueElementId.typeElementPropertyId === undefined
                ) {
                    // return the reference with null property
                    return ref.typeElementPropertyId === null;
                }
                // return the reference with matching property
                return ref.typeElementPropertyId === valueElementId.typeElementPropertyId;
            });

            if (selectedValueReference === undefined) {
                throw new Error(
                    `Could not find value reference id: ${valueElementId.id ?? ''}, propertyId: ${
                        valueElementId.typeElementPropertyId ?? 'null'
                    }`,
                );
            }

            return selectedValueReference.contentType;
        };

        // Enrich any ValueElementIdAPI values to full ValueElementIdReferenceAPI
        const enrichValidationReferences = async (
            validations: PageComponentValidationAPI[],
        ): Promise<PageComponentValidationAPI[]> => {
            const validationsToEnrich = validations.filter(
                (v) => isValueReference(v.value) && !isFullValueReference(v.value),
            );

            if (validationsToEnrich.length === 0) {
                return validations;
            }

            // Get unique value IDs that need enrichment
            const valueIds = [
                ...new Set(
                    validationsToEnrich
                        .map((v) => (v.value as ValueElementIdAPI).id)
                        .filter((id): id is string => id !== null),
                ),
            ];

            if (valueIds.length === 0) {
                return validations;
            }

            // Fetch all references in one request
            const references = await getValueReferenceList({ id: valueIds });

            // Map validations to enriched versions
            return validations.map((validation) => {
                if (!isValueReference(validation.value) || isFullValueReference(validation.value)) {
                    return validation;
                }

                const valueId = validation.value as ValueElementIdAPI;
                const fullRef = references.find((ref) => {
                    if (ref.id !== valueId.id) {
                        return false;
                    }
                    // Empty guid in valueId is equivalent to null
                    return isEmptyGuid(valueId.typeElementPropertyId)
                        ? ref.typeElementPropertyId === null
                        : ref.typeElementPropertyId === valueId.typeElementPropertyId;
                });

                return fullRef ? { ...validation, value: fullRef } : validation;
            });
        };

        if (valueElementId === null) {
            dispatchValidations({ type: 'setContentType', payload: { contentType: null } });
            updateRequired(false);
            updateValidations([]);
        } else {
            setIsLoadingValueReferences(true);
            getContentType(valueElementId)
                .then(async (contentType) => {
                    if (isStale) return;

                    try {
                        const filteredValidations = filterInvalidValidations(
                            contentType,
                            validations,
                        );

                        // Enrich any value references that only have ValueElementIdAPI
                        const enrichedValidations =
                            await enrichValidationReferences(filteredValidations);

                        if (isStale) return;

                        if (
                            enrichedValidations.length !== validations.length ||
                            enrichedValidations.some(
                                (v, i) => v.value !== filteredValidations[i]?.value,
                            )
                        ) {
                            updateValidations(enrichedValidations);
                        }

                        dispatchValidations({ type: 'setContentType', payload: { contentType } });
                    } catch (error) {
                        if (isStale) return;
                        dispatchNotification({
                            type: 'notifyError',
                            payload: { error },
                        });
                    }
                })
                .catch((error) => {
                    if (isStale) return;
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                })
                .finally(() => {
                    if (isStale) return;
                    setIsLoadingValueReferences(false);
                });
        }

        // Cleanup: mark this effect as stale so its callbacks don't update state
        return () => {
            isStale = true;
        };
    }, [valueElementId]);

    if (supportsRequiredValidationOnly) {
        return renderRequiredToggle();
    }

    if (state.viewState === 'hidden') {
        return null;
    }

    return (
        <ConfigSection dataTestId="validation-section" title="Validation">
            {renderRequiredToggle()}
            {state.viewState === 'form' ? (
                <ValidationForm
                    validation={selectedValidation}
                    contentType={state.contentType}
                    onSave={onSave}
                    onCancel={onCancel}
                />
            ) : null}
            {state.viewState === 'list' ? (
                <ValidationList
                    validations={validations}
                    updateValidations={updateValidations}
                    dispatchValidations={dispatchValidations}
                    isLoadingValueReferences={isLoadingValueReferences}
                />
            ) : null}
        </ConfigSection>
    );
};

export default ValidationConfig;
